import { gql } from "@apollo/client";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Collapse,
  Grid,
  Group,
  Loader,
  Menu,
  Paper,
  rem,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { modals } from "@mantine/modals";
import {
  IconChevronDown,
  IconChevronRight,
  IconFilter,
  IconFilterOff,
  IconId,
  IconUser,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelectedOrganizationId } from "../../../../business/OrganizationId";
import { usePlayers } from "../../../../business/players.hook";
import {
  Organization,
  OrganizationIdentityProvider,
  Tournament,
  TournamentTeam,
  TournamentTeamStatus,
} from "../../../../gql/graphql";
import { AutoDataTable } from "../../components/AutoDataTable";
import { useConfirmModal } from "../../components/ConfirmModal";
import { NoTeamsState } from "../../components/EmptyState";
import { ListPageHeader } from "../../components/PageHeader";
import { ListPageSkeleton } from "../../components/SkeletonLoader";
import { StatusBadge } from "../../components/StatusBadge";
import {
  PlayerSearch,
  PlayerSearchSelection,
} from "../../components/form/PlayerSearch";
import { CreateTeamModal } from "./components/CreateTeamModal";
import { GenerateRandomTeamsModal } from "./components/GenerateTeamsModal";
import { TeamJournalDrillDown } from "./TeamJournalDrillDown";

const useStyles = createStyles((theme) => ({
  expansionRoot: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.dark[8],
    borderTop: `1px solid ${theme.colors.dark[5]}`,
  },
  sectionTitle: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    color: theme.colors.dark[2],
    marginBottom: theme.spacing.xs,
  },
  fieldRow: {
    display: "flex",
    alignItems: "baseline",
    padding: `${rem(4)} 0`,
  },
  fieldLabel: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[2],
    minWidth: rem(120),
    flexShrink: 0,
  },
  fieldValue: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[0],
  },
  memberCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.sm,
    transition: "border-color 150ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
    },
  },
  memberAvatar: {
    width: rem(32),
    height: rem(32),
    borderRadius: "50%",
    backgroundColor: theme.colors.cyan[9],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  identitySection: {
    marginTop: theme.spacing.xs,
    paddingTop: theme.spacing.xs,
    borderTop: `1px solid ${theme.colors.dark[5]}`,
  },
  filterToggle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xs,
    padding: `${rem(6)} ${rem(12)}`,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.dark[5]}`,
    backgroundColor: theme.colors.dark[7],
    color: theme.colors.dark[0],
    transition: "all 150ms ease",
    "&:hover": {
      backgroundColor: theme.colors.dark[6],
      borderColor: theme.colors.dark[4],
    },
  },
  filterPanel: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  activeFilterBadge: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  },
  statusMenuItem: {
    "&:hover": {
      backgroundColor: theme.colors.dark[5],
    },
  },
}));

// --- TeamInfo Expansion Component ---

const TEAM_INFO_QUERY = gql`
  query ($id: ID!) {
    tournamentTeam(teamId: $id) {
      id
      customFields {
        name
        value
        property
      }
      members {
        playerProfileId
      }
    }
  }
`;

const TeamInfo = ({ teamId }: { teamId: string }) => {
  const { classes } = useStyles();
  const [playerIds, setPlayerIds] = useState<string[]>([]);
  const [expandedIdentities, setExpandedIdentities] = useState<
    Record<string, boolean>
  >({});

  const { data, loading } = useQuery(
    TEAM_INFO_QUERY as TypedDocumentNode<any>,
    {
      variables: { id: teamId },
    },
  );

  useEffect(() => {
    if (data) {
      setPlayerIds(
        data?.tournamentTeam.members.map(
          (member: any) => member.playerProfileId as string,
        ) ?? [],
      );
    }
  }, [data]);

  const { loading: playersLoading, results } = usePlayers({
    playerIds,
    skip: loading,
  });

  const toggleIdentity = (playerId: string) => {
    setExpandedIdentities((prev) => ({
      ...prev,
      [playerId]: !prev[playerId],
    }));
  };

  if (loading || playersLoading) {
    return (
      <div className={classes.expansionRoot}>
        <Group justify="center" py="md">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading team details...
          </Text>
        </Group>
      </div>
    );
  }

  const customFields = data?.tournamentTeam?.customFields ?? [];
  const members = data?.tournamentTeam?.members ?? [];

  return (
    <div className={classes.expansionRoot}>
      <Grid columns={12}>
        {/* Custom Fields Column */}
        <Grid.Col span={4}>
          <Text className={classes.sectionTitle}>Custom Fields</Text>
          {customFields.length === 0 ? (
            <Text size="sm" c="dimmed" fs="italic">
              No custom fields
            </Text>
          ) : (
            <Stack gap={0}>
              {customFields.map((field: any, idx: any) => (
                <div key={idx} className={classes.fieldRow}>
                  <Text className={classes.fieldLabel}>{field.name}</Text>
                  <Text className={classes.fieldValue}>
                    {field.value || "-"}
                  </Text>
                </div>
              ))}
            </Stack>
          )}
        </Grid.Col>

        {/* Members Column */}
        <Grid.Col span={8}>
          <Text className={classes.sectionTitle}>
            Members ({members.length})
          </Text>
          <Stack gap="xs">
            {members.map((member: any) => {
              const player = results.find(
                (p) => p.id === member.playerProfileId,
              );

              if (!player) {
                return null;
              }

              const hasIdentities =
                player.identities && player.identities.length > 0;
              const isExpanded =
                expandedIdentities[member.playerProfileId as string] ?? false;

              return (
                <Paper
                  key={member.playerProfileId}
                  className={classes.memberCard}
                >
                  <Group gap="sm" align="flex-start">
                    {/* Avatar placeholder */}
                    <ThemeIcon
                      size={32}
                      radius="xl"
                      variant="light"
                      color="cyan"
                    >
                      <IconUser size={16} />
                    </ThemeIcon>

                    {/* Player info */}
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Anchor
                        href={`/players/${member.playerProfileId}`}
                        size="sm"
                        fw={500}
                      >
                        {player.username ?? member.playerProfileId}
                      </Anchor>

                      {/* Player custom fields as badges */}
                      {player.customFields.length > 0 && (
                        <Group gap={4}>
                          {player.customFields.map((field, idx) => (
                            <Badge
                              key={idx}
                              size="xs"
                              variant="outline"
                              color="dark"
                            >
                              {field.name}: {field.value}
                            </Badge>
                          ))}
                        </Group>
                      )}

                      {/* Identities - Collapsible */}
                      {hasIdentities && (
                        <>
                          <UnstyledButton
                            onClick={() =>
                              toggleIdentity(member.playerProfileId as string)
                            }
                            mt={4}
                          >
                            <Group gap={4}>
                              {isExpanded ? (
                                <IconChevronDown size={12} />
                              ) : (
                                <IconChevronRight size={12} />
                              )}
                              <Text size="xs" c="dimmed">
                                {player.identities?.length} identity provider
                                {(player.identities?.length ?? 0) > 1
                                  ? "s"
                                  : ""}
                              </Text>
                            </Group>
                          </UnstyledButton>
                          <Collapse in={isExpanded}>
                            <Stack gap={4} className={classes.identitySection}>
                              {player.identities?.map((identity, idx) => (
                                <div key={idx}>
                                  <Group gap={4} mb={2}>
                                    <IconId size={12} color="grey" />
                                    <Text size="xs" fw={500} c="dimmed">
                                      {
                                        identity.organizationIdentityProvider
                                          ?.name
                                      }
                                    </Text>
                                  </Group>
                                  <Group gap={4} ml="md">
                                    {identity.properties.map((prop, pIdx) => (
                                      <Badge
                                        key={pIdx}
                                        size="xs"
                                        variant="dot"
                                        color="dark"
                                      >
                                        {prop.property}: {prop.value}
                                      </Badge>
                                    ))}
                                  </Group>
                                </div>
                              ))}
                            </Stack>
                          </Collapse>
                        </>
                      )}
                    </Stack>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        </Grid.Col>
      </Grid>
      <Box mt="lg">
        <TeamJournalDrillDown teamId={teamId} />
      </Box>
    </div>
  );
};

// --- Filter Types & Helpers ---

type TeamFilters = {
  filterByStatus?: TournamentTeamStatus;
  filterByCustomFields?: { property: string; value: string }[];
  filterByPlayerUsername?: string;
  filterByPlayerCustomFields?: { property: string; value: string }[];
  filterByPlayers?: PlayerSearchSelection[];
};

const countActiveFilters = (filters?: TeamFilters): number => {
  if (!filters) return 0;
  let count = 0;
  if (filters.filterByStatus) count++;
  if (filters.filterByCustomFields?.length)
    count += filters.filterByCustomFields.length;
  if (filters.filterByPlayerUsername) count++;
  if (filters.filterByPlayerCustomFields?.length)
    count += filters.filterByPlayerCustomFields.length;
  if (filters.filterByPlayers?.length)
    count += filters.filterByPlayers.length;
  return count;
};

// --- Status Menu Component ---

const StatusChangeMenu = ({
  team,
  onStatusChange,
}: {
  team: TournamentTeam;
  onStatusChange: (team: TournamentTeam, status: TournamentTeamStatus) => void;
}) => {
  const availableStatuses = Object.values(TournamentTeamStatus).filter(
    (s) => s !== team.status,
  );

  return (
    <Menu position="bottom-start" shadow="md">
      <Menu.Target>
        <UnstyledButton>
          <StatusBadge status={team.status} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Change status</Menu.Label>
        {availableStatuses.map((status) => (
          <Menu.Item key={status} onClick={() => onStatusChange(team, status)}>
            <StatusBadge status={status} />
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

// --- Active Filter Badge ---

const ActiveFilterBadge = ({
  label,
  value,
  onRemove,
}: {
  label: string;
  value: string;
  onRemove: () => void;
}) => (
  <Badge
    size="sm"
    variant="filled"
    color="dark"
    rightSection={
      <ActionIcon
        size="xs"
        variant="transparent"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <IconX size={10} />
      </ActionIcon>
    }
  >
    {label}: {value || "..."}
  </Badge>
);

// --- Main Component ---

const TOURNAMENT_CONFIG_QUERY = gql`
  query ($id: ID!, $organizationId: ID!) {
    organization(id: $organizationId) {
      configuration {
        customFields {
          name
          property
        }
      }
    }
    tournament(id: $id) {
      configuration {
        customFields {
          name
          property
        }
      }
    }
    identityProviders {
      ... on OrganizationIdentityProvider {
        id
        name
        configuration {
          ... on OAuthClientConfiguration {
            dataRetrievers {
              mappingConfiguration {
                mappings {
                  mappedTo
                }
              }
            }
          }
        }
      }
    }
  }
`;

const TEAMS_LIST_QUERY = gql`
  query (
    $cursor: String
    $count: Float
    $tournamentId: ID!
    $status: TournamentTeamStatus
    $propertiesFilter: [PropertyValueInput!]
    $playerUsernameQuery: String
    $playerPropertiesFilter: [PropertyValueInput!]
    $playerIdentityProviderPropertiesFilter: [IdentityProviderPropertyInput!]
    $playerProfileIds: [ID!]
  ) {
    tournamentTeams(
      page: { first: $count, after: $cursor }
      status: $status
      tournamentId: $tournamentId
      propertiesFilter: $propertiesFilter
      playerUsernameQuery: $playerUsernameQuery
      playerPropertiesFilter: $playerPropertiesFilter
      playerIdentityProviderPropertiesFilter: $playerIdentityProviderPropertiesFilter
      playerProfileIds: $playerProfileIds
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        id
        name
        status
      }
      totalCount
    }
  }
`;

const UPDATE_TEAM_STATUS_MUTATION = gql`
  mutation ($id: ID!, $status: TournamentTeamStatus!) {
    updateTournamentTeamStatus(tournamentTeamId: $id, status: $status) {
      id
      status
    }
  }
`;

export const TournamentTeams = () => {
  const { classes } = useStyles();
  const tournamentId = useParams().tournamentId;
  const navigate = useNavigate();
  const organizationId = useSelectedOrganizationId();
  const { confirm, modal: confirmModal } = useConfirmModal();

  const { data, loading } = useQuery<{
    tournament: Tournament;
    organization: Organization;
    identityProviders: OrganizationIdentityProvider[];
  }>(TOURNAMENT_CONFIG_QUERY, {
    variables: {
      id: tournamentId,
      organizationId,
    },
  });

  const [filters, setFilters] = useState<TeamFilters>({});
  const [debouncedFilters, setDebouncedFilters] = useState<TeamFilters>({});
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(handler);
  }, [filters]);

  const [updateTeamStatus] = useMutation(UPDATE_TEAM_STATUS_MUTATION);

  if (loading) {
    return <ListPageSkeleton />;
  }

  const activeFilterCount = countActiveFilters(filters);

  const clearAllFilters = () => {
    setFilters({});
  };

  const handleStatusChange = async (
    team: TournamentTeam,
    newStatus: TournamentTeamStatus,
  ) => {
    const confirmed = await confirm({
      title: "Change Team Status",
      message: `Are you sure you want to change the status of "${team.name}" from "${team.status}" to "${newStatus}"?`,
      confirmLabel: "Change Status",
      type: "warning",
    });

    if (!confirmed) return;

    await updateTeamStatus({
      variables: {
        id: team.id,
        status: newStatus,
      },
    });
  };

  const removeCustomFieldFilter = (property: string) => {
    const updated = filters.filterByCustomFields?.filter(
      (f) => f.property !== property,
    );
    setFilters({
      ...filters,
      filterByCustomFields: updated && updated.length > 0 ? updated : undefined,
    });
  };

  const removePlayerCustomFieldFilter = (property: string) => {
    const updated = filters.filterByPlayerCustomFields?.filter(
      (f) => f.property !== property,
    );
    setFilters({
      ...filters,
      filterByPlayerCustomFields:
        updated && updated.length > 0 ? updated : undefined,
    });
  };

  const addPlayerFilter = (player: PlayerSearchSelection) => {
    if (filters.filterByPlayers?.some((p) => p.id === player.id)) return;
    setFilters({
      ...filters,
      filterByPlayers: [...(filters.filterByPlayers ?? []), player],
    });
  };

  const removePlayerFilter = (playerId: string) => {
    const updated = filters.filterByPlayers?.filter((p) => p.id !== playerId);
    setFilters({
      ...filters,
      filterByPlayers: updated && updated.length > 0 ? updated : undefined,
    });
  };

  const getIdentityProviderLabel = (property: string): string => {
    if (!property.includes(":")) return property;
    const [providerId, propName] = property.split(":");
    const providerName =
      data?.identityProviders?.find((idp) => idp.id === providerId)?.name ??
      providerId;
    return `${providerName} -> ${propName}`;
  };

  const playerPropertiesFilter =
    debouncedFilters.filterByPlayerCustomFields?.filter(
      (f) => !f.property.includes(":"),
    );
  const playerIdentityProviderPropertiesFilter =
    debouncedFilters.filterByPlayerCustomFields
      ?.filter((field) => field.property.includes(":"))
      .map((field) => {
        const [identityProviderId, property] = field.property.split(":");
        return {
          identityProviderId,
          property,
          value: field.value,
        };
      });

  const hasTeamCustomFields =
    (data?.tournament?.configuration?.customFields?.length ?? 0) > 0;
  const hasPlayerCustomFields =
    (data?.organization?.configuration?.customFields?.length ?? 0) > 0 ||
    (data?.identityProviders?.length ?? 0) > 0;

  const playerFieldOptionsRaw = (
    data?.organization?.configuration?.customFields?.map((field) => ({
      label: field.name,
      value: field.property,
    })) ?? []
  ).concat(
    data?.identityProviders.flatMap(
      (provider) =>
        provider.configuration?.dataRetrievers
          ?.flatMap((retriever) =>
            retriever.mappingConfiguration?.mappings.map((mapping) => ({
              label: `${provider.name} -> ${mapping.mappedTo}`,
              value: `${provider.id}:${mapping.mappedTo}`,
            })),
          )
          .filter((m): m is { label: string; value: string } => !!m) ?? [],
    ) ?? [],
  );

  const playerFieldOptions = playerFieldOptionsRaw.filter(
    (option, index, self) =>
      self.findIndex((o) => o.value === option.value) === index,
  );

  return (
    <div className="wp-fade-in">
      {confirmModal}

      <ListPageHeader
        title="Teams"
        showBreadcrumbs={false}
        actions={[
          {
            label: "Register Team",
            onClick: () => {
              modals.open({
                title: "Register new team",
                children: (
                  <CreateTeamModal tournamentId={tournamentId as string} />
                ),
              });
            },
          },
          {
            label: "Generate Teams",
            variant: "light",
            onClick: () => {
              modals.open({
                title: "Generate random teams",
                centered: true,
                onClose: () => {
                  navigate(0);
                },
                children: (
                  <GenerateRandomTeamsModal
                    tournamentId={tournamentId as string}
                  />
                ),
              });
            },
          },
        ]}
      />

      {/* Filter Toggle & Active Filters */}
      <Group gap="sm" mb="md">
        <UnstyledButton
          className={classes.filterToggle}
          onClick={() => setFiltersOpen((prev) => !prev)}
        >
          <IconFilter size={14} />
          <Text size="sm" fw={500}>
            Filters
          </Text>
          {activeFilterCount > 0 && (
            <Badge size="xs" variant="filled" color="cyan" ml={2}>
              {activeFilterCount}
            </Badge>
          )}
        </UnstyledButton>

        {/* Active filter summary badges */}
        {activeFilterCount > 0 && (
          <>
            {filters.filterByStatus && (
              <ActiveFilterBadge
                label="Status"
                value={filters.filterByStatus}
                onRemove={() =>
                  setFilters({ ...filters, filterByStatus: undefined })
                }
              />
            )}
            {filters.filterByPlayerUsername && (
              <ActiveFilterBadge
                label="Username"
                value={filters.filterByPlayerUsername}
                onRemove={() =>
                  setFilters({ ...filters, filterByPlayerUsername: undefined })
                }
              />
            )}
            {filters.filterByCustomFields?.map((f) => (
              <ActiveFilterBadge
                key={`team-${f.property}`}
                label={f.property}
                value={f.value}
                onRemove={() => removeCustomFieldFilter(f.property)}
              />
            ))}
            {filters.filterByPlayerCustomFields?.map((f) => (
              <ActiveFilterBadge
                key={`player-${f.property}`}
                label={getIdentityProviderLabel(f.property)}
                value={f.value}
                onRemove={() => removePlayerCustomFieldFilter(f.property)}
              />
            ))}
            {filters.filterByPlayers?.map((player) => (
              <ActiveFilterBadge
                key={`player-id-${player.id}`}
                label="Player"
                value={player.username}
                onRemove={() => removePlayerFilter(player.id)}
              />
            ))}

            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<IconFilterOff size={12} />}
              onClick={clearAllFilters}
              color="dimmed"
            >
              Clear all
            </Button>
          </>
        )}
      </Group>

      {/* Collapsible Filter Panel */}
      <Collapse in={filtersOpen}>
        <Paper className={classes.filterPanel} mb="md">
          <Stack gap="md">
            {/* Row 1: Core filters */}
            <Group gap="md" align="flex-end">
              <Select
                label="Status"
                placeholder="All statuses"
                clearable
                size="sm"
                value={filters.filterByStatus ?? null}
                onChange={(value) => {
                  setFilters({
                    ...filters,
                    filterByStatus: value
                      ? (value as TournamentTeamStatus)
                      : undefined,
                  });
                }}
                data={Object.values(TournamentTeamStatus)}
                style={{ minWidth: 160 }}
              />
              <TextInput
                label="Player username"
                placeholder="Search by username..."
                size="sm"
                value={filters.filterByPlayerUsername ?? ""}
                onChange={(event) => {
                  setFilters({
                    ...filters,
                    filterByPlayerUsername:
                      event.currentTarget.value.length > 0
                        ? event.currentTarget.value
                        : undefined,
                  });
                }}
                style={{ flex: 1, maxWidth: 250 }}
              />
              <PlayerSearch
                label="Specific players"
                placeholder="Search a player..."
                size="sm"
                description="Filter to teams that contain one of the selected players"
                onChange={addPlayerFilter}
                resetOnSelect
                style={{ flex: 1, maxWidth: 250 }}
              />
            </Group>

            {/* Row 2: Team custom field filters */}
            {hasTeamCustomFields && (
              <Group gap="md" align="flex-end">
                <Select
                  label="Team custom field"
                  placeholder="Add filter..."
                  clearable
                  size="sm"
                  value={null}
                  onChange={(value) => {
                    if (!value) return;
                    setFilters({
                      ...filters,
                      filterByCustomFields: [
                        ...(filters.filterByCustomFields ?? []).filter(
                          (f) => f.property !== value,
                        ),
                        { property: value, value: "" },
                      ],
                    });
                  }}
                  data={
                    data?.tournament?.configuration?.customFields?.map(
                      (field) => ({
                        label: field.name,
                        value: field.property,
                      }),
                    ) ?? []
                  }
                  style={{ minWidth: 200 }}
                />
                {filters.filterByCustomFields?.map((field) => (
                  <TextInput
                    key={field.property}
                    size="sm"
                    label={
                      <Group gap={4}>
                        <Text size="xs">{field.property}</Text>
                        <ActionIcon
                          color="red"
                          variant="transparent"
                          onClick={() =>
                            removeCustomFieldFilter(field.property)
                          }
                          size="xs"
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      </Group>
                    }
                    value={field.value}
                    placeholder="Value..."
                    onChange={(event) => {
                      setFilters({
                        ...filters,
                        filterByCustomFields: filters.filterByCustomFields?.map(
                          (f) =>
                            f.property === field.property
                              ? {
                                  property: f.property,
                                  value: event.currentTarget.value,
                                }
                              : f,
                        ),
                      });
                    }}
                    style={{ maxWidth: 180 }}
                  />
                ))}
              </Group>
            )}

            {/* Row 3: Player custom field filters */}
            {hasPlayerCustomFields && (
              <Group gap="md" align="flex-end">
                <Select
                  label="Player custom field"
                  placeholder="Add filter..."
                  clearable
                  size="sm"
                  value={null}
                  onChange={(value) => {
                    if (!value) return;
                    setFilters({
                      ...filters,
                      filterByPlayerCustomFields: [
                        ...(filters.filterByPlayerCustomFields ?? []).filter(
                          (f) => f.property !== value,
                        ),
                        { property: value, value: "" },
                      ],
                    });
                  }}
                  data={playerFieldOptions}
                  style={{ minWidth: 200 }}
                />
                {filters.filterByPlayerCustomFields?.map((field) => (
                  <TextInput
                    key={field.property}
                    size="sm"
                    label={
                      <Group gap={4}>
                        <Text size="xs">
                          {getIdentityProviderLabel(field.property)}
                        </Text>
                        <ActionIcon
                          color="red"
                          variant="transparent"
                          onClick={() =>
                            removePlayerCustomFieldFilter(field.property)
                          }
                          size="xs"
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      </Group>
                    }
                    value={field.value}
                    placeholder="Value..."
                    onChange={(event) => {
                      setFilters({
                        ...filters,
                        filterByPlayerCustomFields:
                          filters.filterByPlayerCustomFields?.map((f) =>
                            f.property === field.property
                              ? {
                                  property: f.property,
                                  value: event.currentTarget.value,
                                }
                              : f,
                          ),
                      });
                    }}
                    style={{ maxWidth: 180 }}
                  />
                ))}
              </Group>
            )}
          </Stack>
        </Paper>
      </Collapse>

      <AutoDataTable
        createButtonText={false}
        highlightOnHover
        rowExpansion={(team) => <TeamInfo teamId={team.id} />}
        linkClick={(team) => {
          modals.open({
            title: team.name,
            size: "xl",
            children: <TeamInfo teamId={team.id} />,
          });
        }}
        queryVariables={{
          tournamentId,
          status: debouncedFilters.filterByStatus,
          propertiesFilter: debouncedFilters.filterByCustomFields,
          playerUsernameQuery: debouncedFilters.filterByPlayerUsername,
          playerPropertiesFilter: playerPropertiesFilter?.length
            ? playerPropertiesFilter
            : undefined,
          playerIdentityProviderPropertiesFilter:
            playerIdentityProviderPropertiesFilter?.length
              ? playerIdentityProviderPropertiesFilter
              : undefined,
          playerProfileIds: debouncedFilters.filterByPlayers?.length
            ? debouncedFilters.filterByPlayers.map((p) => p.id)
            : undefined,
        }}
        emptyState={<NoTeamsState />}
        query={TEAMS_LIST_QUERY}
        columns={[
          {
            accessor: "name",
            title: "Name",
          },
          {
            accessor: "status",
            title: "Status",
            rawElement: (item: TournamentTeam) => (
              <StatusChangeMenu
                team={item}
                onStatusChange={handleStatusChange}
              />
            ),
          },
        ]}
      />
    </div>
  );
};
